import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  type ColumnAliases,
  DASHBOARD_DIRECTION_PRESETS,
  DASHBOARD_INTENT_PRESETS,
  type DashboardDetail,
  type DashboardDirection,
  type DashboardIntent,
  deriveDirection,
} from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboard: DashboardDetail;
}

interface AliasEntry {
  sheet: string;
  sqlName: string;
  label: string;
}

function aliasesToEntries(aliases: ColumnAliases): AliasEntry[] {
  const out: AliasEntry[] = [];
  for (const [sheet, map] of Object.entries(aliases)) {
    for (const [sqlName, label] of Object.entries(map)) {
      out.push({ sheet, sqlName, label });
    }
  }
  return out;
}

function entriesToAliases(entries: AliasEntry[]): ColumnAliases {
  const out: ColumnAliases = {};
  for (const e of entries) {
    if (!e.sqlName.trim() || !e.label.trim()) continue;
    const sheetKey = e.sheet.trim() || '__global__';
    const bucket = out[sheetKey] ?? {};
    bucket[e.sqlName.trim()] = e.label.trim();
    out[sheetKey] = bucket;
  }
  return out;
}

/**
 * 분석설계 다이얼로그 (톱니 아이콘 클릭).
 * - Direction 5종 swatch picker
 * - Intent 4종 picker
 * - 컬럼 alias 편집 (sheet / sqlName → 표시라벨)
 *
 * "재생성" 누르면 결정론적 재렌더 (LLM 호출 X) — 단계 3 의 render alias 와 시그니처 정합.
 */
export function DashboardSettingsDialog({ open, onOpenChange, dashboard }: Props) {
  const qc = useQueryClient();
  const [direction, setDirection] = useState<DashboardDirection>(
    dashboard.spec.direction ?? 'modern-minimal',
  );
  const [intent, setIntent] = useState<DashboardIntent>(dashboard.spec.intent ?? 'executive');
  const [aliasEntries, setAliasEntries] = useState<AliasEntry[]>([]);
  const [accentHex, setAccentHex] = useState('');
  const [bgHex, setBgHex] = useState('');
  const [fgHex, setFgHex] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Brand color → deriveDirection 자동 도출 (Dashv2 main.ts:401 패턴).
  const derived = useMemo(() => {
    if (!accentHex.trim()) return null;
    return deriveDirection({
      accentHex: accentHex.trim(),
      bgHex: bgHex.trim() || undefined,
      fgHex: fgHex.trim() || undefined,
    });
  }, [accentHex, bgHex, fgHex]);

  useEffect(() => {
    if (!open) return;
    setDirection(dashboard.spec.direction ?? 'modern-minimal');
    setIntent(dashboard.spec.intent ?? 'executive');
    setAliasEntries(aliasesToEntries(dashboard.columnAliases));
    setAccentHex('');
    setBgHex('');
    setFgHex('');
    setError(null);
  }, [open, dashboard]);

  // brand color 적용 — 도출된 palette 를 모든 chart widget 의 colors 에 일괄 set.
  const applyBrandMutation = useMutation({
    mutationFn: async () => {
      if (!derived) throw new Error('유효한 hex 가 아닙니다.');
      const newSpec = JSON.parse(JSON.stringify(dashboard.spec)) as typeof dashboard.spec;
      const applyToSection = (sections: { widgets: { kind: string; colors?: string[] }[] }[]) => {
        for (const s of sections) {
          for (const w of s.widgets) {
            if (w.kind === 'chart') w.colors = [...derived.palette];
          }
        }
      };
      if (newSpec.tabs?.length) for (const t of newSpec.tabs) applyToSection(t.sections);
      else if (newSpec.sections) applyToSection(newSpec.sections);
      await api.dashboardSetSpec(dashboard.id, { spec: newSpec });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'detail', dashboard.id] });
      qc.invalidateQueries({ queryKey: ['dashboards', 'mine'] });
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : '적용 실패'),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api.dashboardSettings(dashboard.id, {
        direction,
        intent,
        columnAliases: entriesToAliases(aliasEntries),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'detail', dashboard.id] });
      qc.invalidateQueries({ queryKey: ['dashboards', 'mine'] });
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : '저장 실패'),
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: 다른 dialog 와 동일 패턴 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="분석설계"
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-border bg-surface shadow-[var(--shadow-card)] overflow-hidden"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onOpenChange(false);
          }
        }}
      >
        <div className="px-5 pt-5 pb-1 text-sm font-semibold">분석설계</div>
        <div className="px-5 pt-1 pb-3 text-xs text-foreground-muted">
          색감 (direction) / 분석 관점 (intent) / 엑셀 컬럼 라벨을 바꿔 즉시 재생성. LLM 호출 없이
          결정론적으로 다시 렌더됩니다.
        </div>

        <div className="px-5 py-3 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Direction */}
          <div>
            <div className="text-xs font-medium mb-2">색감 (Direction)</div>
            <div className="grid grid-cols-2 gap-2">
              {DASHBOARD_DIRECTION_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setDirection(p.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs',
                    direction === p.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:bg-surface-muted',
                  )}
                >
                  <div className="flex shrink-0">
                    {p.swatch.map((c, i) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: swatch 는 고정 5종, 순서 = 의미
                        key={i}
                        className="size-4 border border-border first:rounded-l last:rounded-r -ml-px first:ml-0"
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{p.label}</div>
                    <div className="truncate text-foreground-muted">{p.hint}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Intent */}
          <div>
            <div className="text-xs font-medium mb-2">분석 관점 (Intent)</div>
            <div className="grid grid-cols-2 gap-2">
              {DASHBOARD_INTENT_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setIntent(p.id)}
                  className={cn(
                    'rounded-md border px-2 py-1.5 text-left text-xs',
                    intent === p.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:bg-surface-muted',
                  )}
                >
                  <div className="font-medium">{p.label}</div>
                  <div className="text-foreground-muted">{p.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Brand color → deriveDirection 자동 5색 도출 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium">사용자 브랜드 색</div>
              <span className="text-[10px] text-foreground-muted">
                hex 입력 → 자동 5색 palette 도출 (Dashv2 deriveDirection)
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="block">
                <span className="block text-[10px] text-foreground-muted mb-0.5">강조 (필수)</span>
                <input
                  type="text"
                  value={accentHex}
                  onChange={(e) => setAccentHex(e.target.value)}
                  placeholder="#2A6BD8"
                  className="w-full h-8 px-2 rounded border border-border bg-background text-xs font-mono outline-none focus:border-accent"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] text-foreground-muted mb-0.5">배경 (선택)</span>
                <input
                  type="text"
                  value={bgHex}
                  onChange={(e) => setBgHex(e.target.value)}
                  placeholder="#fcfcfc"
                  className="w-full h-8 px-2 rounded border border-border bg-background text-xs font-mono outline-none focus:border-accent"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] text-foreground-muted mb-0.5">전경 (선택)</span>
                <input
                  type="text"
                  value={fgHex}
                  onChange={(e) => setFgHex(e.target.value)}
                  placeholder="#1c1c20"
                  className="w-full h-8 px-2 rounded border border-border bg-background text-xs font-mono outline-none focus:border-accent"
                />
              </label>
            </div>
            {derived ? (
              <div className="mt-2 rounded-md border border-border bg-surface-muted/40 p-2">
                <div className="text-[10px] text-foreground-muted mb-1">자동 도출 5색</div>
                <div className="flex gap-1 mb-2">
                  {derived.palette.map((c, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: 5색 고정 순서
                    <div
                      key={i}
                      className="size-7 rounded border border-border"
                      style={{ background: c }}
                      title={c}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => applyBrandMutation.mutate()}
                  disabled={applyBrandMutation.isPending}
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40"
                >
                  <Wand2 className="size-3" />
                  {applyBrandMutation.isPending ? '적용 중…' : '이 색을 모든 차트에 적용'}
                </button>
              </div>
            ) : accentHex.trim() ? (
              <div className="mt-2 text-[11px] text-destructive">유효한 hex (예: #2A6BD8)</div>
            ) : null}
          </div>

          {/* 컬럼 alias */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium">컬럼 라벨</div>
              <button
                type="button"
                onClick={() =>
                  setAliasEntries((prev) => [...prev, { sheet: '', sqlName: '', label: '' }])
                }
                className="inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
              >
                <Plus className="size-3" /> 추가
              </button>
            </div>
            <div className="text-[11px] text-foreground-muted mb-2">
              엑셀 컬럼명이 잘못 인식된 경우 (예: cal12 → 매출액) 표시라벨로 치환. sheet 비어두면
              모든 시트에 적용.
            </div>
            {aliasEntries.length === 0 ? (
              <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-foreground-muted">
                편집할 alias 가 없습니다. 추가 버튼으로 시작하세요.
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5 text-[10px] text-foreground-muted px-1">
                  <div>시트</div>
                  <div>원본 컬럼명</div>
                  <div>표시라벨</div>
                  <div />
                </div>
                {aliasEntries.map((e, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: 정렬 없음, 순서 안정
                  <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5">
                    <input
                      type="text"
                      value={e.sheet}
                      onChange={(ev) =>
                        setAliasEntries((prev) => {
                          const next = [...prev];
                          if (next[idx]) next[idx] = { ...next[idx], sheet: ev.target.value };
                          return next;
                        })
                      }
                      placeholder="(공용)"
                      className="h-8 px-2 rounded-md border border-border bg-background text-xs outline-none focus:border-accent"
                    />
                    <input
                      type="text"
                      value={e.sqlName}
                      onChange={(ev) =>
                        setAliasEntries((prev) => {
                          const next = [...prev];
                          if (next[idx]) next[idx] = { ...next[idx], sqlName: ev.target.value };
                          return next;
                        })
                      }
                      placeholder="cal12"
                      className="h-8 px-2 rounded-md border border-border bg-background text-xs outline-none focus:border-accent font-mono"
                    />
                    <input
                      type="text"
                      value={e.label}
                      onChange={(ev) =>
                        setAliasEntries((prev) => {
                          const next = [...prev];
                          if (next[idx]) next[idx] = { ...next[idx], label: ev.target.value };
                          return next;
                        })
                      }
                      placeholder="매출액"
                      className="h-8 px-2 rounded-md border border-border bg-background text-xs outline-none focus:border-accent"
                    />
                    <button
                      type="button"
                      onClick={() => setAliasEntries((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label="제거"
                      className="grid size-8 place-items-center rounded-md text-foreground-muted hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error ? <div className="text-xs text-destructive">{error}</div> : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-8 px-3 rounded-md text-sm text-foreground-muted hover:bg-surface-muted"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending}
            className="h-8 px-3 rounded-md text-sm bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40"
          >
            {saveMutation.isPending ? '재생성 중…' : '재생성'}
          </button>
        </div>
      </div>
    </div>
  );
}
